import { SsoEmailDomain } from '@choco-admin/types';
import { Button, Chip, Switch } from '@heroui/react';
import React, { useMemo, useState } from 'react';
import { FiEdit2 } from 'react-icons/fi';

import { useSsoEmailDomains } from '../../hooks';
import { normalizeEmailDomain } from '../../utils';

import { EmailDomainsModal } from './EmailDomainsModal';
import { EmailDomainsTableHeader } from './EmailDomainsTableHeader';

export interface EmailDomainsFieldProps {
  connectionId: string;
  /** Cognito's list, in Cognito's own spelling, one entry per routed domain. */
  emailDomains: SsoEmailDomain[];
}

/**
 * The domains this connection signs people in for, and whether each one may
 * only sign in through the provider.
 *
 * Two lists make up the rows. Cognito's routed domains are the ones that can be
 * switched on. Beside them sit domains that still hold a stored flag after
 * Cognito stopped routing them: they no longer do anything, and clearing the
 * flag is the only way to be rid of them.
 *
 * A saved change is held here rather than re-read. The flag is stored behind a
 * secondary index, so a read taken straight after the write can still answer
 * with the old value and would undo what the person just saved on screen.
 */
export const EmailDomainsField: React.FC<EmailDomainsFieldProps> = ({
  connectionId,
  emailDomains,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const { emailDomains: flaggedDomains, refetch } = useSsoEmailDomains({
    connectionId,
  });

  const routedDomains = useMemo(
    () =>
      emailDomains.map((domain) => ({
        ...domain,
        exclusiveFederatedLogin:
          saved[domain.emailDomain] ?? domain.exclusiveFederatedLogin,
      })),
    [emailDomains, saved]
  );

  // A stored flag is keyed on the normalised domain, while Cognito's routed
  // spelling is whatever the provider was set up with. Both sides go through
  // the same normalisation the backend uses, so one domain is never two rows.
  const staleDomains = useMemo(() => {
    const routedKeys = new Set(
      emailDomains.map((domain) => normalizeEmailDomain(domain.emailDomain))
    );

    return flaggedDomains.filter(
      (domain) =>
        !routedKeys.has(normalizeEmailDomain(domain.emailDomain)) &&
        (saved[domain.emailDomain] ?? domain.exclusiveFederatedLogin)
    );
  }, [emailDomains, flaggedDomains, saved]);

  const allDomains = useMemo(
    () => [...routedDomains, ...staleDomains],
    [routedDomains, staleDomains]
  );

  const staleKeys = useMemo(
    () => new Set(staleDomains.map((domain) => domain.emailDomain)),
    [staleDomains]
  );

  if (allDomains.length === 0) {
    return (
      <span className="text-default-500">
        No email domains route to this connection.
      </span>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border border-default-200">
        <EmailDomainsTableHeader />
        <div className="divide-y divide-default-200">
          {allDomains.map((domain) => (
            <div
              key={domain.emailDomain}
              className="flex items-center justify-between gap-4 px-4 py-2.5"
            >
              <span className="flex items-center gap-2">
                <span className="font-mono text-sm text-foreground">
                  {domain.emailDomain}
                </span>
                {staleKeys.has(domain.emailDomain) && (
                  <Chip size="sm" variant="flat" color="warning">
                    No longer used by this connection
                  </Chip>
                )}
              </span>
              {/* No control for a de-routed domain: the setting no longer
                  applies to anything, so there is no state worth showing. The
                  tag says what it is and the editor is where it is cleared.
                  Routed rows are disabled rather than read-only, because a
                  read-only switch keeps full opacity and reads as clickable. */}
              {!staleKeys.has(domain.emailDomain) && (
                <Switch
                  size="sm"
                  isDisabled
                  aria-label={`Exclusive SSO login for ${domain.emailDomain}`}
                  isSelected={domain.exclusiveFederatedLogin}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <Button
        size="sm"
        variant="flat"
        startContent={<FiEdit2 className="h-3.5 w-3.5" />}
        onPress={() => setIsEditing(true)}
      >
        Edit domains
      </Button>

      {isEditing && (
        <EmailDomainsModal
          isOpen
          onClose={() => setIsEditing(false)}
          connectionId={connectionId}
          emailDomains={allDomains}
          staleDomains={staleKeys}
          onSaved={(changes) => {
            setSaved((current) => ({ ...current, ...changes }));
            refetch();
          }}
        />
      )}
    </div>
  );
};
